import { useMemo, useState } from 'react';
import {
  buildAssembly,
  getPath,
  PARAM_PATHS,
  testPresetRange,
  validatePresetContent,
  type EvaluatedCabinet,
  type ParametricPresetContent,
  type PresetContent,
  type PresetParamDef,
} from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';
import { CATEGORY_LABEL } from './LibraryPanel';
import { newUnitId, selectionToAssembly } from './presets';
import type { EditorStore } from './store';

interface Candidate {
  path: string;
  label: string;
  dim: 'length' | 'count';
  current: number;
  on: boolean;
  min: string;
  max: string;
}

const DEFAULT_ON = new Set(['width', 'height', 'depth', 'shelves.count']);

function keyFromPath(path: string) {
  return path.replace(/\.(\w)/g, (_, c: string) => c.toUpperCase()).replace(/[^a-zA-Z0-9_]/g, '');
}

function candidates(unit: Extract<EvaluatedCabinet, { kind: 'cabinet' }>): Candidate[] {
  const params = unit.instance.params;
  return Object.entries(PARAM_PATHS)
    .filter(([path]) => typeof getPath(params, path) === 'number')
    .map(([path, meta]) => {
      const cur = getPath(params, path) as number;
      const isLen = meta.dim === 'length';
      return {
        path,
        label: meta.label,
        dim: meta.dim,
        current: cur,
        on: DEFAULT_ON.has(path),
        // A zero length (e.g. no plinth) gets a 0–200 mm range instead of an empty one.
        min: isLen ? (cur === 0 ? '0' : String(Math.max(1, Math.round((cur * 0.5) / 1000)))) : '0',
        max: isLen ? (cur === 0 ? '200' : String(Math.round((cur * 1.5) / 1000))) : String(cur + 3),
      };
    });
}

export function PresetWizard({
  units,
  exec,
  snapshot,
  onClose,
  onSaved,
}: {
  units: EvaluatedCabinet[];
  exec: EditorStore['exec'];
  snapshot(): Promise<Blob | null>;
  onClose(): void;
  onSaved(p: PresetSummaryDto): void;
}) {
  const single = units.length === 1 ? units[0]! : null;
  const parametric = single?.kind === 'cabinet';
  const [name, setName] = useState(single ? single.instance.name.replace(/^[A-Z]+\d+\s*/, '') || single.instance.name : 'Moj sklop');
  const [category, setCategory] = useState<PresetSummaryDto['category']>(parametric ? 'base' : 'custom');
  const [scope, setScope] = useState<'private' | 'workspace'>('private');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [link, setLink] = useState(true);
  const [cands, setCands] = useState<Candidate[]>(() => (parametric ? candidates(single as Extract<EvaluatedCabinet, { kind: 'cabinet' }>) : []));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const asm = useMemo(() => (parametric ? null : selectionToAssembly(units)), [parametric, units]);

  const built = useMemo((): { content: PresetContent | null; problems: string[]; cases: { label: string; errors: string[] }[] } => {
    if (parametric) {
      const problems: string[] = [];
      const params: PresetParamDef[] = [];
      for (const c of cands.filter((x) => x.on)) {
        const min = Number(c.min.replace(',', '.'));
        const max = Number(c.max.replace(',', '.'));
        if (!Number.isFinite(min) || !Number.isFinite(max)) {
          problems.push(`${c.label}: min/max nisu brojevi.`);
          continue;
        }
        const scale = c.dim === 'length' ? 1000 : 1;
        params.push({ key: keyFromPath(c.path), label: c.label, path: c.path, dim: c.dim, default: c.current, min: Math.round(min * scale), max: Math.round(max * scale), step: c.dim === 'length' ? 1000 : 1 });
      }
      const content: ParametricPresetContent = {
        schemaVersion: 1,
        kind: 'parametric',
        base: structuredClone((single as Extract<EvaluatedCabinet, { kind: 'cabinet' }>).instance.params),
        params,
        derived: [],
        materials: { ...single!.instance.materials },
        notes: [],
      };
      problems.push(...validatePresetContent(content));
      const cases = problems.length ? [] : testPresetRange(content);
      return { content, problems, cases };
    }
    const content: PresetContent = { schemaVersion: 1, kind: 'assembly', panels: asm!.panels, materials: { ...(units[0]?.instance.materials ?? {}) }, notes: [] };
    const r = buildAssembly({ id: 'test', name: 'test', panels: asm!.panels, transform: { position: [0, 0, 0], rotationY: 0, mirrored: false }, materials: {}, edgeOverrides: {}, hidden: false, locked: false, environment: 'dry' as const, preset: null });
    return { content, problems: [...validatePresetContent(content)], cases: [{ label: 'Statički sklop', errors: r.issues.filter((i) => i.severity === 'error').map((i) => i.message) }] };
  }, [parametric, cands, single, asm, units]);

  const blocked = !built.content || built.problems.length > 0 || built.cases.some((c) => c.errors.length > 0) || !name.trim();

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      let thumbnailAssetId: string | null = null;
      const blob = await snapshot();
      if (blob) thumbnailAssetId = (await Api.uploadAsset(blob, 'preset.png')).id;
      const saved = await Api.createPreset({
        name: name.trim(),
        category,
        scope,
        description,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 20),
        content: built.content!,
        thumbnailAssetId,
        message: 'Kreiran iz selekcije',
      });
      if (link) {
        if (parametric) {
          const content = built.content as ParametricPresetContent;
          exec(`Poveži sa presetom ${saved.name}`, (d) => {
            const c = d.cabinets.find((x) => x.id === single!.instance.id)!;
            c.preset = { presetId: saved.id, versionId: saved.latestVersionId, version: 1, values: Object.fromEntries(content.params.map((p) => [p.key, p.default])) };
          });
        } else {
          const ids = new Set(units.map((u) => u.instance.id));
          exec(`Zamijeni selekciju sklopom ${saved.name}`, (d) => {
            const id = newUnitId(d, 's');
            d.cabinets = d.cabinets.filter((c) => !ids.has(c.id));
            d.assemblies = d.assemblies.filter((a) => !ids.has(a.id));
            d.assemblies.push({
              id,
              name: `${id.toUpperCase()} ${saved.name}`,
              panels: structuredClone(asm!.panels),
              transform: { position: asm!.origin, rotationY: 0, mirrored: false },
              materials: { ...(units[0]?.instance.materials ?? {}) },
              edgeOverrides: {},
              hidden: false,
              locked: false,
              environment: 'dry' as const,
              preset: { presetId: saved.id, versionId: saved.latestVersionId, version: 1 },
            });
          });
        }
      }
      onSaved(saved);
    } catch (e) {
      const err = e as Error & { body?: { errors?: string[] } };
      setError([err.message, ...(err.body?.errors ?? [])].join(' '));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal wide" role="dialog" aria-modal aria-label="Kreiraj preset iz selekcije" onClick={(e) => e.stopPropagation()}>
        <h2>Kreiraj preset iz selekcije</h2>
        <p className="muted">
          {parametric
            ? `Parametarski preset iz elementa "${single!.instance.name}". Izaberite koje mjere korisnik može mijenjati i u kom opsegu.`
            : `Statički sklop od ${units.reduce((s, u) => s + u.result.parts.length, 0)} ploča (${units.length} element/a). Ishodište je lijevi-zadnji-donji ugao selekcije; odnosi među pločama se ne pretvaraju u pravila dok ih ne definišete.`}
        </p>

        <h3 style={{ marginTop: 14 }}>1. Opis</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label className="field">
            <span>Naziv</span>
            <input className="input" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          </label>
          <label className="field">
            <span>Kategorija</span>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as PresetSummaryDto['category'])}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Dostupnost</span>
            <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'private' | 'workspace')}>
              <option value="private">Lični (samo ja)</option>
              <option value="workspace">Radionica (svi članovi)</option>
            </select>
          </label>
          <label className="field">
            <span>Tagovi (zarezom)</span>
            <input className="input" value={tags} onChange={(e) => setTags(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          </label>
        </div>
        <label className="field">
          <span>Opis</span>
          <textarea className="input" style={{ height: 56, padding: 6 }} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
        </label>

        {parametric && (
          <>
            <h3 style={{ marginTop: 6 }}>2. Izloženi parametri</h3>
            <div className="param-grid">
              <span />
              <span className="head">Parametar</span>
              <span className="head">Sada</span>
              <span className="head">Min</span>
              <span className="head">Max</span>
              {cands.map((c, i) => (
                <FragmentRow key={c.path} c={c} onChange={(patch) => setCands((all) => all.map((x, j) => (j === i ? { ...x, ...patch } : x)))} />
              ))}
            </div>
            <p className="faint" style={{ fontSize: 11 }}>
              Ostale vrijednosti (konstrukcija, raspored fronti, materijali) ostaju fiksne kao u izabranom elementu.
            </p>
          </>
        )}
        {asm && asm.warnings.length > 0 && (
          <ul className="muted" style={{ fontSize: 12 }}>
            {asm.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}

        <h3 style={{ marginTop: 10 }}>{parametric ? '3.' : '2.'} Provjera min/default/max</h3>
        {built.problems.map((p) => (
          <p key={p} className="error-text">
            {p}
          </p>
        ))}
        <table className="grid">
          <tbody>
            {built.cases.map((c) => (
              <tr key={c.label}>
                <td style={{ width: 200 }}>{c.label}</td>
                <td className={c.errors.length ? 'case-err' : 'case-ok'} style={{ whiteSpace: 'normal' }}>
                  {c.errors.length ? c.errors.slice(0, 3).join(' ') : 'Ispravno'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
          <input type="checkbox" checked={link} onChange={(e) => setLink(e.target.checked)} />
          {parametric ? 'Poveži ovaj element sa novim presetom' : 'Zamijeni selekciju instancom novog sklopa'}
        </label>
        {error && <p className="error-text">{error}</p>}
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Otkaži
          </button>
          <button className="btn primary" disabled={blocked || busy} onClick={save} title={blocked ? 'Ispravite nevažeće konfiguracije' : undefined}>
            {busy ? 'Snimanje…' : 'Snimi preset (verzija 1)'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FragmentRow({ c, onChange }: { c: Candidate; onChange(p: Partial<Candidate>): void }) {
  const fmt = c.dim === 'length' ? `${c.current / 1000} mm` : String(c.current);
  return (
    <>
      <input type="checkbox" checked={c.on} onChange={(e) => onChange({ on: e.target.checked })} aria-label={`Izloži ${c.label}`} />
      <span>{c.label}</span>
      <span className="mono faint">{fmt}</span>
      <input className="input" style={{ height: 24 }} disabled={!c.on} value={c.min} onChange={(e) => onChange({ min: e.target.value })} onKeyDown={(e) => e.stopPropagation()} aria-label={`${c.label} min`} />
      <input className="input" style={{ height: 24 }} disabled={!c.on} value={c.max} onChange={(e) => onChange({ max: e.target.value })} onKeyDown={(e) => e.stopPropagation()} aria-label={`${c.label} max`} />
    </>
  );
}
